
import { useEffect, useMemo, useState, useRef } from 'react';
import { apiCreateNote, apiDeleteNote, apiEditNote, apiGetNotes } from './api';
import { nodesToDict  } from './utilsData';

export const useNodeNotes = (graphData, upsertGraphData) => {
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [crtNoteDialogNote, setCrtNoteDialogNote] = useState({});
  const [crtNoteDialogParams, setCrtNoteDialogParams] = useState({});
  const [noteServerRequests, setNoteServerRequests] = useState(0);
  const [noteServerError, setNoteServerError] = useState('');

  const serverNotes = useRef({});
  const queriedNodesDict = useRef({});

  function upsertNotesIntoNodes() {
    const nodeDict = nodesToDict(graphData.nodes || []);
    const nodeUpdatesDict = {};
    for (let noteId in serverNotes.current) {
      const note = serverNotes.current[noteId];
      const node = nodeDict[note.node_type]?.[note.node_value];
      if (!node) continue;
      if (!(node.type in nodeUpdatesDict)) nodeUpdatesDict[node.type] = {};
      if (!(node.value in nodeUpdatesDict[node.type])) nodeUpdatesDict[node.type][node.value] = {type: node.type, value: node.value, notes: {}};
      nodeUpdatesDict[node.type][node.value].notes[noteId] = note;
    }
    const nodeUpdates = [];
    for (let nodeType in nodeUpdatesDict) {
      for (let nodeValue in nodeUpdatesDict[nodeType]) {
        nodeUpdates.push(nodeUpdatesDict[nodeType][nodeValue]);
      }
    }
    upsertGraphData(nodeUpdates, []);
  }

  function createNote(node) {
    setCrtNoteDialogNote({ node_type: node.type, node_value: node.value, is_public: false });
    setCrtNoteDialogParams({});
    setShowNoteDialog(true);
  }

  function editNote(note) {
    setCrtNoteDialogNote({...note});
    setCrtNoteDialogParams({});
    setShowNoteDialog(true);
  }

  function noteDlgClose() {
    setCrtNoteDialogNote({});
    setCrtNoteDialogParams({});
    setShowNoteDialog(false);
  }

  function noteDlgSave(note) {
    if (!note.content) {
      setCrtNoteDialogParams(p => ({...p, errorMsg: 'Note content is required'}));
      return;
    }
    setCrtNoteDialogParams(p => ({...p, loading: true}))

    if (!note.id) {
      apiCreateNote(note)
        .then(newServerNote => {
          noteDlgClose();
          serverNotes.current[newServerNote.id] = newServerNote;
          upsertNotesIntoNodes();
        })
        .catch(e => {
          setCrtNoteDialogParams(p => ({...p, loading: false, errorMsg: e.message}))
          console.log("Error saving note: ", e)
        })
    } else {
      apiEditNote(note)
        .then(newServerNote => {
          noteDlgClose();
          serverNotes.current[newServerNote.id] = newServerNote;
          upsertNotesIntoNodes();
        })
        .catch(e => {
          setCrtNoteDialogParams(p => ({...p, loading: false, errorMsg: e.message}))
          console.log("Error saving note: ", e)
        })
    }
  }

  function noteDlgDelete(note) {
    noteDlgClose();
    serverNotes.current[note.id].deleted = true;
    upsertNotesIntoNodes();

    apiDeleteNote(note)
      .then(() => {
        console.log('Deleted note')
      })
      .catch(e => {
        console.warn("Error deleting note: ", e)
      })
  }

  function implRefreshNotes(nodes) {
    nodes.forEach(node => {
      if (!(node.type in queriedNodesDict.current)) queriedNodesDict.current[node.type] = {};
      queriedNodesDict.current[node.type][node.value] = 1;
    })
    console.log("Getting notes for ", nodes.length, " nodes");

    setNoteServerRequests(c => c + 1);
    apiGetNotes(nodes)
      .then(notes => {
        console.log("    -->  Got ", notes.length, " notes");
        setNoteServerRequests(c => c - 1); setNoteServerError('');
        notes.forEach(note => { serverNotes.current[note.id] = note; });
        upsertNotesIntoNodes();
        console.log("Notes: ", notes);
      })
      .catch(e => {
        setNoteServerRequests(c => c - 1); setNoteServerError(e.message);
        console.warn("Error getting notes: ", e);
      })
  }

  function refreshNotes() {
    if (!graphData?.nodes) return;
    implRefreshNotes(graphData.nodes);
  }

  useEffect(() => {
    if (!graphData?.nodes) return;
    const newNodes = [];
    const crtNodesKeys = {};
    for (let node of graphData.nodes) {
      if (!(node.type in crtNodesKeys)) crtNodesKeys[node.type] = {};
      crtNodesKeys[node.type][node.value] = 1;
      if (queriedNodesDict.current[node.type]?.[node.value]) continue;
      newNodes.push(node);
    }
    // unmark deleted nodes as already queried, in case the user adds the node again
    const deletedNodes = [];
    for (let nt in queriedNodesDict.current) {
      for (let nv in queriedNodesDict.current[nt]) {
        if (crtNodesKeys[nt]?.[nv]) continue;
        deletedNodes.push({type: nt, value: nv});
      }
    }
    deletedNodes.forEach(node => {
      delete queriedNodesDict.current[node.type][node.value];
    });

    console.log("Graph data changed. Nodes: ", graphData.nodes, "NEW: ", newNodes, "Edges: ", graphData.edges);
    if (newNodes.length === 0) return;
    
    implRefreshNotes(newNodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData])

  return {
    showNoteDialog,
    crtNoteDialogNote,
    crtNoteDialogParams,
    noteDlgClose, noteDlgSave, noteDlgDelete,

    createNote, editNote, refreshNotes,

    noteServerRequests, noteServerError
  }
}
